import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clipboard } from 'react-feather';

export const VerificationCodeInputField = ({
  length = 6,
  onChange,
  onAutoSubmit,
}) => {
  const [verificationCode, setVerificationCode] = useState(
    new Array(length).fill('')
  );
  const [shake, setShake] = useState(false);
  const [clipboardCode, setClipboardCode] = useState(null);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Autofocus on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    onChange(verificationCode.join(''));

    if (verificationCode.every((val) => val !== '')) {
      onAutoSubmit?.(verificationCode.join('')).catch(() => {
        triggerShake();
      });
    }
  }, [verificationCode, onChange, onAutoSubmit]);

  useEffect(() => {
    const checkClipboard = async () => {
      try {
        const text = await navigator.clipboard.readText();
        const cleaned = text.replace(/\D/g, '').slice(0, length);
        if (cleaned.length === length) {
          setClipboardCode(cleaned);
        }
      } catch (err) {
        console.log('Clipboard read blocked or not allowed.');
      }
    };

    checkClipboard();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (verificationCode[index] === '') {
        if (index > 0) {
          inputRefs.current[index - 1]?.focus();
        }
      } else {
        const newCode = [...verificationCode];
        newCode[index] = '';
        setVerificationCode(newCode);
      }
    }
  };

  const handlePaste = (e) => {
    const pastedCode = e.clipboardData
      .getData('Text')
      .replace(/\D/g, '')
      .slice(0, length);
    if (pastedCode.length === length) {
      setVerificationCode(pastedCode.split(''));
    }
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const handleClipboardFill = () => {
    setVerificationCode(clipboardCode.split(''));
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        >
        <motion.div
          
          className="grid grid-cols-[repeat(auto-fit,minmax(55px,1fr))] gap-3 w-full max-w-md mx-auto"
          animate={shake ? { x: [0, -10, 10, -8, 8, -4, 4, 0] } : { x: 0 }}
          transition={{ duration: 0.6 }}
        >
          {verificationCode.map((digit, index) => (
            <motion.input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              id={`verification-input-${index}`}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              className="aspect-square text-center text-3xl font-semibold rounded-xl border border-white/20 bg-white/10 dark:bg-white/5 text-white shadow-inner backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-[#01BAFF]/40 transition-all"
              whileFocus={{ scale: 1.12 }}
            />
          ))}
        </motion.div>
      </motion.div>

      {/* Display suggested clipboard code */}
      <AnimatePresence>
        {clipboardCode && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="mt-4 cursor-pointer flex flex-row items-center space-x-2 bg-[#01BAFF20] text-white text-xs px-4 py-2 rounded-lg shadow-md"
            onClick={handleClipboardFill}
          >
            <Clipboard size={12} />
            
             <strong>{clipboardCode}</strong>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
